import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetAllClientIdentityUsersQueryResponse, GetAllClientIdentityUsersPathParams, GetAllClientIdentityUsersQueryParams } from "../types/GetAllClientIdentityUsers";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetAllClientIdentityUsersClient = typeof client<GetAllClientIdentityUsersQueryResponse, never, never>;
type GetAllClientIdentityUsers = {
    data: GetAllClientIdentityUsersQueryResponse;
    error: never;
    request: never;
    pathParams: GetAllClientIdentityUsersPathParams;
    queryParams: GetAllClientIdentityUsersQueryParams;
    headerParams: never;
    response: GetAllClientIdentityUsersQueryResponse;
    client: {
        parameters: Partial<Parameters<GetAllClientIdentityUsersClient>[0]>;
        return: Awaited<ReturnType<GetAllClientIdentityUsersClient>>;
    };
};
export const getAllClientIdentityUsersQueryKey = (clientCode: GetAllClientIdentityUsersPathParams["clientCode"], params?: GetAllClientIdentityUsers["queryParams"]) => [{ url: "/api/clients/:clientCode/identities/users", params: { clientCode: clientCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientIdentityUsersQueryKey = ReturnType<typeof getAllClientIdentityUsersQueryKey>;
export function getAllClientIdentityUsersQueryOptions(clientCode: GetAllClientIdentityUsersPathParams["clientCode"], params?: GetAllClientIdentityUsers["queryParams"], options: GetAllClientIdentityUsers["client"]["parameters"] = {}) {
    const queryKey = getAllClientIdentityUsersQueryKey(clientCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientIdentityUsers["data"], GetAllClientIdentityUsers["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/identities/users`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets all user identities for a given client. The API Key or user that
is calling this endpoint must have access to the client with the given
client code.
 * @summary Get all users
 * @link /api/clients/:clientCode/identities/users
 */
export function useGetAllClientIdentityUsers<TData = GetAllClientIdentityUsers["response"], TQueryData = GetAllClientIdentityUsers["response"], TQueryKey extends QueryKey = GetAllClientIdentityUsersQueryKey>(clientCode: GetAllClientIdentityUsersPathParams["clientCode"], params?: GetAllClientIdentityUsers["queryParams"], options: {
    query?: Partial<QueryObserverOptions<GetAllClientIdentityUsers["response"], GetAllClientIdentityUsers["error"], TData, TQueryData, TQueryKey>>;
    client?: GetAllClientIdentityUsers["client"]["parameters"];
} = {}): UseQueryResult<TData, GetAllClientIdentityUsers["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientIdentityUsersQueryKey(clientCode, params);
    const query = useQuery({
        ...getAllClientIdentityUsersQueryOptions(clientCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetAllClientIdentityUsers["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getAllClientIdentityUsersSuspenseQueryKey = (clientCode: GetAllClientIdentityUsersPathParams["clientCode"], params?: GetAllClientIdentityUsers["queryParams"]) => [{ url: "/api/clients/:clientCode/identities/users", params: { clientCode: clientCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientIdentityUsersSuspenseQueryKey = ReturnType<typeof getAllClientIdentityUsersSuspenseQueryKey>;
export function getAllClientIdentityUsersSuspenseQueryOptions(clientCode: GetAllClientIdentityUsersPathParams["clientCode"], params?: GetAllClientIdentityUsers["queryParams"], options: GetAllClientIdentityUsers["client"]["parameters"] = {}) {
    const queryKey = getAllClientIdentityUsersSuspenseQueryKey(clientCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientIdentityUsers["data"], GetAllClientIdentityUsers["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/identities/users`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets all user identities for a given client. The API Key or user that
is calling this endpoint must have access to the client with the given
client code.
 * @summary Get all users
 * @link /api/clients/:clientCode/identities/users
 */
export function useGetAllClientIdentityUsersSuspense<TData = GetAllClientIdentityUsers["response"], TQueryKey extends QueryKey = GetAllClientIdentityUsersSuspenseQueryKey>(clientCode: GetAllClientIdentityUsersPathParams["clientCode"], params?: GetAllClientIdentityUsers["queryParams"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetAllClientIdentityUsers["response"], GetAllClientIdentityUsers["error"], TData, TQueryKey>>;
    client?: GetAllClientIdentityUsers["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetAllClientIdentityUsers["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientIdentityUsersSuspenseQueryKey(clientCode, params);
    const query = useSuspenseQuery({
        ...getAllClientIdentityUsersSuspenseQueryOptions(clientCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetAllClientIdentityUsers["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}